import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, Typography,
} from '@mui/material';
import { saveGabbaiMemberRegistration } from '../gabbaiPortalApi';
import { getGabbaiTheme } from '../gabbaiPortalTheme';
import PortalSearchSelect from '../PortalSearchSelect';
import { formatHebrewDate } from '../../../utils/hebrewDate';

function formatExamDate(date) {
  return formatHebrewDate(date);
}

export default function MemberRegisterDialog({ open, member, meta, onClose, onSaved }) {
  const t = getGabbaiTheme();
  const openExams = meta?.openExams || [];
  const examCities = meta?.examCities || [];
  const cityOptions = useMemo(
    () => examCities.map((city) => ({ id: city, label: city })),
    [examCities]
  );

  const initialIds = useMemo(
    () => (member?.registrations || []).map((r) => String(r.examId)).filter(Boolean),
    [member]
  );
  const initialLocation = useMemo(() => {
    const fromReg = member?.registrations?.find((r) => r.location)?.location;
    if (fromReg && examCities.includes(fromReg)) return fromReg;
    if (member?.city && examCities.includes(member.city)) return member.city;
    return examCities[0] || '';
  }, [member, examCities]);

  const [selectedIds, setSelectedIds] = useState([]);
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelectedIds(initialIds);
    setLocation(initialLocation);
    setError('');
  }, [open, initialIds, initialLocation]);

  const toggleExam = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    setError('');
    if (!selectedIds.length) {
      setError('יש לבחור לפחות מבחן אחד');
      return;
    }
    if (!location) {
      setError('יש לבחור מוקד בחינה');
      return;
    }
    setLoading(true);
    try {
      await saveGabbaiMemberRegistration(member.id, {
        examIds: selectedIds,
        location,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירת הרישום');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      dir="rtl"
      PaperProps={{
        sx: {
          background: t.dialogBg,
          border: `1px solid ${t.dialogBorder}`,
          color: '#f5f0e6',
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        רישום למבחנים
        {member ? ` — ${member.firstName} ${member.lastName}` : ''}
      </DialogTitle>
      <DialogContent>
        {!meta?.activePeriod && (
          <Alert severity="warning" sx={{ mb: 2 }}>אין תקופת מבחנים פעילה</Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <PortalSearchSelect
          label="מוקד בחינה"
          options={cityOptions}
          value={location}
          onChange={setLocation}
          placeholder="חיפוש עיר..."
          sx={{ mb: 2, mt: 1 }}
        />

        <Typography sx={{ mb: 1, color: 'rgba(245,240,230,0.75)', fontWeight: 600 }}>
          מבחנים פתוחים
        </Typography>
        {!openExams.length && (
          <Typography sx={{ color: 'rgba(245,240,230,0.55)' }}>אין מבחנים פתוחים כרגע</Typography>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {openExams.map((exam) => {
            const id = String(exam.id);
            const checked = selectedIds.includes(id);
            return (
              <FormControlLabel
                key={id}
                control={(
                  <Checkbox
                    checked={checked}
                    onChange={() => toggleExam(id)}
                    sx={{
                      color: t.checkboxIdle,
                      '&.Mui-checked': { color: t.checkbox },
                    }}
                  />
                )}
                label={(
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      {exam.code ? `${exam.code} · ` : ''}{exam.name} {exam.subject || ''}
                    </Typography>
                    {exam.date && (
                      <Typography sx={{ fontSize: '0.8rem', color: 'rgba(245,240,230,0.55)' }}>
                        {formatExamDate(exam.date)}
                      </Typography>
                    )}
                  </Box>
                )}
                sx={{ alignItems: 'flex-start', m: 0, py: 0.5 }}
              />
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} sx={{ color: 'rgba(245,240,230,0.7)' }}>
          ביטול
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading || !openExams.length}
          sx={{
            fontWeight: 700,
            color: t.ctaColor,
            background: t.ctaBg,
            px: 2.5,
            '&:hover': { filter: 'brightness(1.06)' },
            '&.Mui-disabled': { background: t.ctaDisabledBg, color: t.ctaDisabledColor },
          }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: t.ctaColor }} /> : 'שמירה'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
